import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, FilePdf, CurrencyDollar } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export default function PagoDetail() {
  const { id } = useParams();
  const [pago, setPago] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPago();
  }, [id]);

  const loadPago = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE}/nomina/pagos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPago(response.data);
    } catch (error) {
      toast.error('Error al cargar el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadNomina = async () => {
    try {
      toast.loading('Generando PDF...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reports/nomina/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nomina_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.dismiss();
      toast.success('PDF descargado');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al generar PDF');
    }
  };

  const handleDownloadVolantes = async () => {
    try {
      toast.loading('Generando volantes...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reports/nomina/${id}/volantes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `volantes_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.dismiss();
      toast.success('Volantes descargados');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al generar volantes');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  if (!pago) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Pago no encontrado</p>
          <Link to="/nomina">
            <Button className="mt-4">Volver a Nómina</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="pago-detail">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <Link
              to="/nomina"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
            >
              <ArrowLeft size={14} />
              Volver a Nómina
            </Link>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Detalle de Pago
            </h1>
            <p className="text-muted-foreground">
              {pago.tipo_pago === 'quincenal' ? 'Quincenal' : 'Mensual'} - {formatDate(pago.fecha_pago)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadNomina} data-testid="download-nomina">
              <FilePdf size={18} className="mr-2" />
              Descargar Nómina
            </Button>
            <Button variant="outline" onClick={handleDownloadVolantes} data-testid="download-volantes">
              <FilePdf size={18} className="mr-2" />
              Descargar Volantes
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Empleados</p>
              <p className="metric-value text-slate-600">{pago.empleados?.length || 0}</p>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Total Bruto</p>
              <p className="metric-value text-[#F69031]">{formatCurrency(pago.total_bruto)}</p>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Deducciones</p>
              <p className="metric-value text-red-500">-{formatCurrency(pago.total_deducciones)}</p>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Total a Pagar</p>
              <p className="metric-value text-[#E73425]">{formatCurrency(pago.total_neto)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Employees Table */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle>Detalle por Empleado</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b table-header">
                    <th className="p-3 text-left">#</th>
                    <th className="p-3 text-left">Nombre</th>
                    <th className="p-3 text-left">Apodo</th>
                    <th className="p-3 text-left">Cédula</th>
                    <th className="p-3 text-left">Proyecto</th>
                    <th className="p-3 text-left">Puesto</th>
                    <th className="p-3 text-right">S. Quinc.</th>
                    <th className="p-3 text-right">Comis.</th>
                    <th className="p-3 text-right">AFP</th>
                    <th className="p-3 text-right">ARS</th>
                    <th className="p-3 text-right">CXC</th>
                    <th className="p-3 text-right">Seg.Med</th>
                    <th className="p-3 text-right font-bold">A Pagar</th>
                  </tr>
                </thead>
                <tbody>
                  {pago.empleados?.map((emp, idx) => (
                    <tr key={emp.empleado_id} className="border-b table-row">
                      <td className="p-3">{idx + 1}</td>
                      <td className="p-3 font-medium">{emp.nombre_completo}</td>
                      <td className="p-3 text-muted-foreground">{emp.apodo || '-'}</td>
                      <td className="p-3 font-mono text-xs">{emp.cedula || '-'}</td>
                      <td className="p-3 text-xs">{emp.proyecto || 'GENERAL'}</td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-xs">{emp.puesto}</Badge>
                      </td>
                      <td className="p-3 text-right font-mono">{formatCurrency(emp.sueldo_quincenal)}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(emp.comisiones || 0)}</td>
                      <td className="p-3 text-right font-mono text-red-500">{formatCurrency(emp.afp || 0)}</td>
                      <td className="p-3 text-right font-mono text-red-500">{formatCurrency(emp.ars || 0)}</td>
                      <td className="p-3 text-right font-mono text-red-500">{formatCurrency(emp.cxc || 0)}</td>
                      <td className="p-3 text-right font-mono text-red-500">{formatCurrency(emp.seg_medico || 0)}</td>
                      <td className="p-3 text-right font-bold font-mono text-[#E73425]">
                        {formatCurrency(emp.sueldo_pagar)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/50 font-bold">
                    <td colSpan={6} className="p-3 text-right">TOTALES:</td>
                    <td className="p-3 text-right font-mono">
                      {formatCurrency(pago.empleados?.reduce((s, e) => s + (e.sueldo_quincenal || 0), 0))}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {formatCurrency(pago.empleados?.reduce((s, e) => s + (e.comisiones || 0), 0))}
                    </td>
                    <td className="p-3 text-right font-mono text-red-500">
                      {formatCurrency(pago.empleados?.reduce((s, e) => s + (e.afp || 0), 0))}
                    </td>
                    <td className="p-3 text-right font-mono text-red-500">
                      {formatCurrency(pago.empleados?.reduce((s, e) => s + (e.ars || 0), 0))}
                    </td>
                    <td className="p-3 text-right font-mono text-red-500">
                      {formatCurrency(pago.empleados?.reduce((s, e) => s + (e.cxc || 0), 0))}
                    </td>
                    <td className="p-3 text-right font-mono text-red-500">
                      {formatCurrency(pago.empleados?.reduce((s, e) => s + (e.seg_medico || 0), 0))}
                    </td>
                    <td className="p-3 text-right font-mono text-[#E73425] text-lg">
                      {formatCurrency(pago.total_neto)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
